// ─────────────────────────────────────────────────────────────────────────────
// codex_renderer.ts — Codex: spell reference, invocations, arcanes
//
// Spell pages show 4 spells in a 2×2 grid. Each cell has a header strip
// (family glyph + name + tier badge), a looping cast-curve animation, and
// a one-line description.  The layout auto-paginates.
//
// Special pages: Welcome (intro), Invocations (tap sequences), Arcanes (runes).
// ─────────────────────────────────────────────────────────────────────────────

import type { PageLayout } from './page_renderer';
import { CAST_CURVES, sampleCurve, type CastCurve } from './cast_curves';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface CodexEntry {
  id: string;
  name: string;
  tier: number;
  family: string;
  lore: string;
  params: Array<{ name: string; description: string }>;
}

/** Tap-sequence invocation shown in the Invocations codex page. */
export interface InvocationEntry {
  id: string;
  name: string;
  /** Sequence notation: 'O' = quick tap, 'L' = long tap, '_' = pause. */
  sequence: string[];
  color: string;
  description: string;
}

/** Rune-in-a-box arcane shown in the Arcanes codex page. */
export interface ArcaneEntry {
  id: string;
  name: string;
  symbol: string;          // display glyph or label for the symbol
  description: string;
}

/** One row in the Contents (table-of-contents) codex page. */
export interface ContentsEntry {
  /** Stable id — section id for section rows, page id for page rows. */
  id: string;
  /** Display label. */
  title: string;
  /** 0 = section heading, 1 = page under a section, or top-level page. */
  level: number;
  /** True for section headings (not directly navigable unless pageId is set). */
  isSection: boolean;
  /**
   * Page id to navigate to when clicked. For section rows this is the section's
   * header page id (if any). Undefined rows are non-navigable.
   */
  pageId?: string;
  /** Accent color for section rows. */
  color?: string;
}

/** A hit rect produced by rendering the Contents page, for click dispatch. */
export interface ContentsHitRect {
  pageId: string;
  x: number; y: number; w: number; h: number;
}

/** One codex page = one spell. */
export interface CodexPage {
  entry: CodexEntry;
  curve: CastCurve | null;
}

// ── Visual constants ──────────────────────────────────────────────────────────

const FAMILY_GLYPH: Record<string, { icon: string; color: string }> = {
  act:       { icon: '⊙', color: 'rgba(220, 180, 60, 0.9)' },
  bind:      { icon: '⊗', color: 'rgba(100, 160, 220, 0.9)' },
  transmute: { icon: '☿', color: 'rgba(180, 80, 200, 0.9)' },
  invoke:    { icon: '✦', color: 'rgba(80, 220, 140, 0.9)' },
};

const FAMILY_COLOR: Record<string, string> = {
  act: '#c8a030', bind: '#5a8fc0', transmute: '#a050c0', invoke: '#40b870',
};

const TIER_LABEL: Record<number, string> = {
  0: 'Cantrip', 1: 'Common', 2: 'Compound', 3: 'Arcane',
};

const TIER_ORNAMENT_COLOR: Record<number, string> = {
  0: 'rgba(140, 120, 80, 0.4)',
  1: 'rgba(180, 160, 60, 0.5)',
  2: 'rgba(100, 160, 220, 0.5)',
  3: 'rgba(200, 80, 200, 0.5)',
};

/** Duration of one full animation loop in seconds. */
const ANIM_LOOP_S = 2.8;
/** Pause at end before restarting, in seconds. */
const ANIM_PAUSE_S = 0.8;
const ANIM_TOTAL_S = ANIM_LOOP_S + ANIM_PAUSE_S;

/** Spells per codex page (2 cols × 2 rows). */
const SPELLS_PER_PAGE = 4;
const GRID_COLS = 2;
const GRID_PAD = 10;

// ── Renderer ──────────────────────────────────────────────────────────────────

export class CodexRenderer {
  private _entries: CodexEntry[] = [];
  private _pages: CodexPage[] = [];
  private _invocations: InvocationEntry[] = [];
  private _arcanes: ArcaneEntry[] = [];
  private _contents: ContentsEntry[] = [];
  /** Hit rects produced by the most recent Contents draw (canvas-absolute). */
  private _contentsHitRects: ContentsHitRect[] = [];

  update(entries: CodexEntry[]): void {
    this._entries = entries;
    this._pages = entries.map(e => ({
      entry: e,
      curve: CAST_CURVES[e.id] ?? null,
    }));
  }

  updateInvocations(invocations: InvocationEntry[]): void {
    this._invocations = invocations;
  }

  updateArcanes(arcanes: ArcaneEntry[]): void {
    this._arcanes = arcanes;
  }

  /** Update the table-of-contents rows shown on the Contents codex page. */
  updateContents(entries: ContentsEntry[]): void {
    this._contents = entries;
  }

  get entryCount(): number { return this._entries.length; }
  get pages(): CodexPage[] { return this._pages; }
  /** Hit rects (canvas-absolute) produced by the most recent Contents draw. */
  get contentsHitRects(): ContentsHitRect[] { return this._contentsHitRects; }

  /** The codex is now a single Contents page — Spells / Invocations /
   *  Arcanes / Welcome are being redesigned and don't render their own
   *  codex pages any longer. */
  get totalPageCount(): number {
    return 1;
  }

  /** Label for a given page index (only one — Contents). */
  pageLabel(_index: number): string {
    return 'Contents';
  }

  // ── Draw one page ─────────────────────────────────────────────────────────

  drawPage(
    ctx: CanvasRenderingContext2D,
    layout: PageLayout,
    pageIndex: number,
    t: number,
  ): void {
    const cX = layout.x + layout.marginLeft;
    const cY = layout.y + layout.marginTop;
    const cW = layout.width - layout.marginLeft - layout.marginRight;
    const cH = layout.height - layout.marginTop - layout.marginBottom;

    ctx.save();

    // ── Decorative header ───────────────────────────────────────────────
    const headerCx = layout.x + layout.width / 2;
    const headerY = cY - 36;

    // Ornamental rule above title
    ctx.strokeStyle = 'rgba(139, 99, 64, 0.18)';
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(headerCx - 120, headerY - 4);
    ctx.lineTo(headerCx + 120, headerY - 4);
    ctx.stroke();
    // Centre diamond
    ctx.fillStyle = 'rgba(139, 99, 64, 0.25)';
    ctx.save();
    ctx.translate(headerCx, headerY - 4);
    ctx.rotate(Math.PI / 4);
    ctx.fillRect(-2.5, -2.5, 5, 5);
    ctx.restore();

    ctx.font = 'small-caps bold 18px Georgia, serif';
    ctx.fillStyle = 'rgba(100, 70, 30, 0.8)';
    ctx.textBaseline = 'top';
    ctx.textAlign = 'center';
    ctx.fillText('⟠  The Codex  ⟠', headerCx, headerY);

    ctx.font = 'italic 11px Georgia, serif';
    ctx.fillStyle = 'rgba(100, 70, 30, 0.5)';
    // Lightweight subtitle — the old spells/invocations/arcanes counts are
    // retired since those views are being redesigned.
    const pageCount = this._contents.length;
    ctx.fillText(
      pageCount > 0 ? `${pageCount} ${pageCount === 1 ? 'entry' : 'entries'}` : 'no pages yet',
      headerCx, headerY + 20,
    );

    // Ornamental rule below subtitle
    ctx.strokeStyle = 'rgba(139, 99, 64, 0.12)';
    ctx.beginPath();
    ctx.moveTo(headerCx - 90, headerY + 36);
    ctx.lineTo(headerCx + 90, headerY + 36);
    ctx.stroke();
    ctx.textAlign = 'left';

    // ── Inner margin border (faint inset frame) ──────────────────────────
    ctx.strokeStyle = 'rgba(139, 99, 64, 0.08)';
    ctx.lineWidth = 0.5;
    roundRect(ctx, cX - 2, cY - 2, cW + 4, cH + 4, 4);
    ctx.stroke();

    // ── Clip ────────────────────────────────────────────────────────────
    ctx.beginPath();
    ctx.rect(cX - 4, cY, cW + 8, cH);
    ctx.clip();

    // ── Footer: page number ─────────────────────────────────────────────
    ctx.font = 'italic 10px Georgia, serif';
    ctx.fillStyle = 'rgba(100, 70, 30, 0.35)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillText(`— ${pageIndex + 1} —`, headerCx, cY + cH - 2);

    // ── Route to the right page renderer ────────────────────────────────
    // The codex now has a single page: Contents.  Spells / Invocations /
    // Arcanes / Welcome are retired pending a redesign.
    this._drawContentsPage(ctx, cX, cY, cW, cH);

    ctx.restore();
  }

  // ── Contents (table-of-contents) page ───────────────────────────────────
  //
  // Lists the journal's sections and pages as a navigable tree. Each row
  // with a resolved pageId becomes a click target via _contentsHitRects.

  private _drawContentsPage(
    ctx: CanvasRenderingContext2D,
    cX: number, cY: number, cW: number, cH: number,
  ): void {
    // Reset hit rects for this frame
    this._contentsHitRects = [];

    const cx = cX + cW / 2;

    // Section title
    ctx.font = 'small-caps bold 15px Georgia, serif';
    ctx.fillStyle = 'rgba(100, 70, 30, 0.75)';
    ctx.textBaseline = 'top';
    ctx.textAlign = 'center';
    ctx.fillText('❧  Contents  ❧', cx, cY + 4);

    ctx.font = 'italic 10px Georgia, serif';
    ctx.fillStyle = 'rgba(100, 70, 30, 0.45)';
    ctx.fillText('All sections and pages in this journal', cx, cY + 24);
    ctx.textAlign = 'left';

    // Ornamental divider
    this._drawOrnamentalDivider(ctx, cx, cY + 40, 120);

    // Empty state
    if (this._contents.length === 0) {
      ctx.font = 'italic 11px Georgia, serif';
      ctx.fillStyle = 'rgba(100, 70, 30, 0.5)';
      ctx.textAlign = 'center';
      ctx.fillText('No pages yet — start writing to populate the book.', cx, cY + 80);
      ctx.textAlign = 'left';
      return;
    }

    // Rows
    const listX = cX + 24;
    const listW = cW - 48;
    const rowH = 22;
    const sectionRowH = 26;
    let y = cY + 58;
    const maxY = cY + cH - 8;

    for (const entry of this._contents) {
      if (y + rowH > maxY) {
        // Overflow indicator
        ctx.font = 'italic 10px Georgia, serif';
        ctx.fillStyle = 'rgba(100, 70, 30, 0.45)';
        ctx.fillText('…', listX + 4, y);
        break;
      }

      const thisRowH = entry.isSection ? sectionRowH : rowH;
      const indent = Math.max(0, entry.level) * 16;
      const rowX = listX + indent;
      const rowW = listW - indent;

      if (entry.isSection) {
        // Section heading — larger, colored, with color swatch
        const swatchR = 5;
        if (entry.color) {
          ctx.fillStyle = entry.color;
          ctx.globalAlpha = 0.75;
          ctx.beginPath();
          ctx.arc(rowX + swatchR, y + thisRowH / 2, swatchR, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1;
        }
        ctx.font = 'small-caps bold 13px Georgia, serif';
        ctx.fillStyle = 'rgba(60, 40, 20, 0.85)';
        ctx.textBaseline = 'middle';
        const title = truncate(ctx, entry.title, rowW - swatchR * 2 - 12);
        ctx.fillText(title, rowX + swatchR * 2 + 8, y + thisRowH / 2);

        // Subtle underline
        ctx.strokeStyle = 'rgba(139, 99, 64, 0.15)';
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        ctx.moveTo(rowX, y + thisRowH - 2);
        ctx.lineTo(rowX + rowW, y + thisRowH - 2);
        ctx.stroke();

        if (entry.pageId) {
          this._contentsHitRects.push({
            pageId: entry.pageId,
            x: rowX, y, w: rowW, h: thisRowH,
          });
        }
      } else {
        // Page row — dot + title
        const dotR = 2;
        ctx.fillStyle = 'rgba(100, 70, 30, 0.5)';
        ctx.beginPath();
        ctx.arc(rowX + 4, y + thisRowH / 2, dotR, 0, Math.PI * 2);
        ctx.fill();

        ctx.font = '12px Georgia, serif';
        ctx.fillStyle = '#3a2510';
        ctx.globalAlpha = 0.85;
        ctx.textBaseline = 'middle';
        const title = truncate(ctx, entry.title || '(untitled)', rowW - 16);
        ctx.fillText(title, rowX + 14, y + thisRowH / 2);
        ctx.globalAlpha = 1;

        if (entry.pageId) {
          this._contentsHitRects.push({
            pageId: entry.pageId,
            x: rowX, y, w: rowW, h: thisRowH,
          });
        }
      }

      y += thisRowH + 2;
    }

    ctx.textBaseline = 'top';
  }

  // ── Welcome page ─────────────────────────────────────────────────────────

  private _drawWelcomePage(
    ctx: CanvasRenderingContext2D,
    cX: number, cY: number, cW: number, _cH: number,
  ): void {
    const cx = cX + cW / 2;
    let y = cY + 28;

    // Corner flourishes
    this._drawCornerFlourish(ctx, cX + 4, cY + 4, 1, 1);
    this._drawCornerFlourish(ctx, cX + cW - 4, cY + 4, -1, 1);
    this._drawCornerFlourish(ctx, cX + 4, cY + _cH - 4, 1, -1);
    this._drawCornerFlourish(ctx, cX + cW - 4, cY + _cH - 4, -1, -1);

    // Title
    ctx.font = 'bold 28px Georgia, serif';
    ctx.fillStyle = '#3a2510';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.globalAlpha = 0.85;
    ctx.fillText('The Grimoire', cx, y);
    y += 42;

    // Ornamental divider
    this._drawOrnamentalDivider(ctx, cx, y, 140);
    y += 16;

    // Subtitle
    ctx.font = 'italic 13px Georgia, serif';
    ctx.fillStyle = '#5a4020';
    ctx.globalAlpha = 0.65;
    ctx.fillText('An interactive notebook for the curious mind.', cx, y);
    y += 16;
    ctx.fillText('Draw, write, cast spells, and discover.', cx, y);
    y += 36;

    // Body text — left-aligned
    ctx.textAlign = 'left';
    ctx.font = '12px Georgia, serif';
    ctx.fillStyle = '#3a2510';
    ctx.globalAlpha = 0.8;

    const body = [
      'Welcome to your Grimoire — a canvas notebook where ink meets code.',
      '',
      'Everything here lives on a page. Draw with your stylus or mouse,',
      'and the book remembers every stroke. Blocks of text, code, and',
      'formulas sit alongside your ink.',
      '',
      'Pages are grouped into Sections. Use the tabs above to navigate',
      'between them, or create new sections for your work.',
      '',
      '── Quick Start ──',
      '',
      '  Draw: Touch the page and draw. Strokes are saved automatically.',
      '  Stylus Menu: Triple-tap to open the tool menu.',
      '  Select: Draw a circle around content to select it.',
      '  Erase: Switch to the eraser and strike through strokes.',
      '  Text: Tap a text block to edit with your keyboard.',
      '  Spells: Draw gestures to cast spells.',
      '  Wizard: Summon the Wizard by invoking the wizard spell.',
    ];

    const leftX = cX + 24;
    for (const line of body) {
      if (line.startsWith('──')) {
        ctx.font = 'small-caps bold 12px Georgia, serif';
        ctx.fillStyle = '#7a5830';
        ctx.globalAlpha = 0.7;
        ctx.fillText(line, leftX, y);
        ctx.font = '12px Georgia, serif';
        ctx.fillStyle = '#3a2510';
        ctx.globalAlpha = 0.8;
      } else {
        ctx.fillText(line, leftX, y);
      }
      y += line === '' ? 10 : 17;
    }
  }

  // ── Decorative helpers ────────────────────────────────────────────────────

  /** Small L-shaped corner flourish. sx/sy: 1 or -1 for direction. */
  private _drawCornerFlourish(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, sx: number, sy: number,
  ): void {
    ctx.save();
    ctx.strokeStyle = 'rgba(139, 99, 64, 0.15)';
    ctx.lineWidth = 0.75;
    ctx.beginPath();
    ctx.moveTo(x, y + sy * 18);
    ctx.lineTo(x, y);
    ctx.lineTo(x + sx * 18, y);
    ctx.stroke();
    ctx.restore();
  }

  /** Centred ornamental divider — line with diamond in the middle. */
  private _drawOrnamentalDivider(
    ctx: CanvasRenderingContext2D,
    cx: number, y: number, halfW: number,
  ): void {
    ctx.save();
    ctx.strokeStyle = 'rgba(139, 99, 64, 0.2)';
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(cx - halfW, y);
    ctx.lineTo(cx - 6, y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx + 6, y);
    ctx.lineTo(cx + halfW, y);
    ctx.stroke();
    // Diamond
    ctx.fillStyle = 'rgba(139, 99, 64, 0.3)';
    ctx.save();
    ctx.translate(cx, y);
    ctx.rotate(Math.PI / 4);
    ctx.fillRect(-3, -3, 6, 6);
    ctx.restore();
    ctx.restore();
  }

  // ── Spell grid (2×2) ───────────────────────────────────────────────────────

  private _drawSpellGrid(
    ctx: CanvasRenderingContext2D,
    cX: number, cY: number, cW: number, cH: number,
    startIdx: number, endIdx: number,
    t: number,
  ): void {
    const count = endIdx - startIdx;
    const rows = Math.ceil(count / GRID_COLS);
    const cellW = Math.floor((cW - GRID_PAD * (GRID_COLS + 1)) / GRID_COLS);
    const cellH = Math.floor((cH - GRID_PAD * (rows + 1) - 20) / rows); // 20px for section heading
    const headingY = cY + 4;

    // Section heading
    ctx.font = 'small-caps 12px Georgia, serif';
    ctx.fillStyle = 'rgba(100, 70, 30, 0.5)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText('✦  Spells  ✦', cX + cW / 2, headingY);
    ctx.textAlign = 'left';

    const gridTop = headingY + 22;

    for (let i = 0; i < count; i++) {
      const page = this._pages[startIdx + i];
      if (!page) continue;
      const { entry, curve } = page;
      const col = i % GRID_COLS;
      const row = Math.floor(i / GRID_COLS);
      const cx = cX + GRID_PAD + col * (cellW + GRID_PAD);
      const cy = gridTop + row * (cellH + GRID_PAD);
      const famColor = FAMILY_COLOR[entry.family] ?? '#a08050';

      // Stagger animation per cell so they don't all sync
      const stagger = i * 0.7;
      const cellT = t + stagger;

      this._drawSpellCell(ctx, entry, curve, famColor, cx, cy, cellW, cellH, cellT);
    }
  }

  // ── Single spell cell ──────────────────────────────────────────────────────

  private _drawSpellCell(
    ctx: CanvasRenderingContext2D,
    entry: CodexEntry,
    curve: CastCurve | null,
    famColor: string,
    x: number, y: number, w: number, h: number,
    t: number,
  ): void {
    ctx.save();

    // Card background
    ctx.fillStyle = 'rgba(235, 220, 190, 0.4)';
    roundRect(ctx, x, y, w, h, 6);
    ctx.fill();

    // Tier ornament — left edge
    const tierColor = TIER_ORNAMENT_COLOR[entry.tier] ?? TIER_ORNAMENT_COLOR[0];
    ctx.fillStyle = tierColor;
    roundRect(ctx, x, y, 4, h, 3);
    ctx.fill();

    // Faint card border
    ctx.strokeStyle = 'rgba(139, 99, 64, 0.12)';
    ctx.lineWidth = 0.5;
    roundRect(ctx, x, y, w, h, 6);
    ctx.stroke();

    // ── Header strip (top 28px) ──────────────────────────────────────────
    const headerH = 28;

    // Family glyph
    const glyph = FAMILY_GLYPH[entry.family] ?? FAMILY_GLYPH.act;
    ctx.font = '15px Georgia, serif';
    ctx.fillStyle = glyph.color;
    ctx.textBaseline = 'middle';
    ctx.fillText(glyph.icon, x + 10, y + headerH / 2 + 1);

    // Spell name
    ctx.font = 'bold 11px Georgia, serif';
    ctx.fillStyle = '#3a2510';
    const maxNameW = w - 60;
    const name = truncate(ctx, entry.name, maxNameW);
    ctx.fillText(name, x + 28, y + headerH / 2 + 1);

    // Tier badge (top-right)
    const tierLabel = TIER_LABEL[entry.tier] ?? `T${entry.tier}`;
    ctx.font = 'small-caps 8px Georgia, serif';
    ctx.fillStyle = 'rgba(100, 80, 50, 0.55)';
    ctx.textAlign = 'right';
    ctx.fillText(tierLabel, x + w - 8, y + headerH / 2 + 1);
    ctx.textAlign = 'left';

    // Divider
    ctx.strokeStyle = 'rgba(139, 99, 64, 0.12)';
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(x + 8, y + headerH);
    ctx.lineTo(x + w - 8, y + headerH);
    ctx.stroke();

    // ── Animation area (middle section) ──────────────────────────────────
    const animTop = y + headerH + 4;
    const descH = 28;  // reserve for bottom description
    const animH = h - headerH - descH - 8;

    if (curve && curve.points.length >= 2 && animH > 30) {
      this._drawCellAnim(ctx, curve, famColor, x + 8, animTop, w - 16, animH, t);
    } else {
      // No curve — show a faded placeholder glyph
      ctx.globalAlpha = 0.12;
      ctx.font = '36px Georgia, serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = famColor;
      ctx.fillText(glyph.icon, x + w / 2, animTop + animH / 2);
      ctx.textAlign = 'left';
      ctx.globalAlpha = 1;
    }

    // ── Description (bottom) ─────────────────────────────────────────────
    const descY = y + h - descH + 4;
    ctx.font = 'italic 9px Georgia, serif';
    ctx.fillStyle = '#5a4020';
    ctx.globalAlpha = 0.65;
    const desc = truncate(ctx, entry.lore, w - 20);
    ctx.fillText(desc, x + 10, descY + 6);
    ctx.globalAlpha = 1;

    // Params count hint (bottom-right, very subtle)
    if (entry.params.length > 0) {
      ctx.font = '8px Georgia, serif';
      ctx.fillStyle = 'rgba(100, 70, 30, 0.35)';
      ctx.textAlign = 'right';
      ctx.fillText(`${entry.params.length} param${entry.params.length > 1 ? 's' : ''}`, x + w - 8, descY + 18);
      ctx.textAlign = 'left';
    }

    ctx.restore();
  }

  // ── Cell animation (compact, looping cast trace) ──────────────────────────

  private _drawCellAnim(
    ctx: CanvasRenderingContext2D,
    curve: CastCurve,
    color: string,
    x: number, y: number, w: number, h: number,
    t: number,
  ): void {
    ctx.save();

    const loopT = (t % ANIM_TOTAL_S) / ANIM_LOOP_S;
    const progress = Math.min(loopT, 1);

    const pad = Math.min(w, h) * 0.08;
    const drawX = x + pad;
    const drawY = y + pad;
    const drawW = w - pad * 2;
    const drawH = h - pad * 2;

    // Faint region background
    ctx.fillStyle = 'rgba(240, 230, 210, 0.25)';
    roundRect(ctx, x, y, w, h, 4);
    ctx.fill();

    // Ghost of full curve (very faint)
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.globalAlpha = 0.1;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.beginPath();
    for (let i = 0; i < curve.points.length; i++) {
      const px = drawX + curve.points[i][0] * drawW;
      const py = drawY + curve.points[i][1] * drawH;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    if (curve.closed) ctx.closePath();
    ctx.stroke();

    // Animated stroke (revealed portion)
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.globalAlpha = 0.75;
    const steps = Math.max(2, Math.floor(progress * 60));
    ctx.beginPath();
    for (let i = 0; i <= steps; i++) {
      const st = i / 60;
      if (st > progress) break;
      const [nx, ny] = sampleCurve(curve, st);
      const px = drawX + nx * drawW;
      const py = drawY + ny * drawH;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.stroke();

    // Pen tip
    if (progress < 1) {
      const [tipX, tipY] = sampleCurve(curve, progress);
      ctx.fillStyle = color;
      ctx.globalAlpha = 0.85;
      ctx.beginPath();
      ctx.arc(drawX + tipX * drawW, drawY + tipY * drawH, 3, 0, Math.PI * 2);
      ctx.fill();
    }

    // Start marker
    const [sx, sy] = curve.points[0];
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.globalAlpha = 0.4;
    ctx.beginPath();
    ctx.arc(drawX + sx * drawW, drawY + sy * drawH, 3.5, 0, Math.PI * 2);
    ctx.stroke();

    ctx.restore();
  }

  // ── Invocations page ────────────────────────────────────────────────────

  private _drawInvocationsPage(
    ctx: CanvasRenderingContext2D,
    cX: number, cY: number, cW: number, cH: number,
  ): void {
    const cx = cX + cW / 2;

    // Section title
    ctx.font = 'small-caps bold 15px Georgia, serif';
    ctx.fillStyle = 'rgba(100, 70, 30, 0.75)';
    ctx.textBaseline = 'top';
    ctx.textAlign = 'center';
    ctx.fillText('✦  Invocations  ✦', cx, cY + 4);

    ctx.font = 'italic 10px Georgia, serif';
    ctx.fillStyle = 'rgba(100, 70, 30, 0.45)';
    ctx.fillText('Timed tap sequences that cast an action', cx, cY + 24);
    ctx.textAlign = 'left';

    // Ornamental divider
    this._drawOrnamentalDivider(ctx, cx, cY + 40, 100);

    // Grid layout: 2 columns, up to 6 rows
    const COLS = 2;
    const PAD = 14;
    const cellW = Math.floor((cW - PAD * 3) / COLS);
    const cellH = 64;
    const startY = cY + 52;

    for (let i = 0; i < this._invocations.length; i++) {
      const inv = this._invocations[i];
      const col = i % COLS;
      const row = Math.floor(i / COLS);
      const cx = cX + PAD + col * (cellW + PAD);
      const cy = startY + row * (cellH + 10);
      if (cy + cellH > cY + cH) break;

      // Card background
      ctx.fillStyle = 'rgba(235, 220, 190, 0.45)';
      roundRect(ctx, cx, cy, cellW, cellH, 5);
      ctx.fill();

      // Color accent bar
      ctx.fillStyle = inv.color;
      ctx.globalAlpha = 0.7;
      roundRect(ctx, cx, cy, 4, cellH, 2);
      ctx.fill();
      ctx.globalAlpha = 1;

      // Name
      ctx.font = 'bold 12px Georgia, serif';
      ctx.fillStyle = '#3a2510';
      ctx.textBaseline = 'top';
      ctx.fillText(inv.name, cx + 12, cy + 6);

      // Tap sequence notation: O = empty circle (quick), ● = solid (long), _ = gap
      const seqY = cy + 26;
      let seqX = cx + 14;
      const dotR = 5;
      const gap = 14;

      for (const tok of inv.sequence) {
        if (tok === 'O') {
          // Empty circle — quick tap
          ctx.strokeStyle = inv.color;
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.arc(seqX, seqY, dotR, 0, Math.PI * 2);
          ctx.stroke();
          seqX += gap;
        } else if (tok === 'L') {
          // Solid circle — long tap
          ctx.fillStyle = inv.color;
          ctx.beginPath();
          ctx.arc(seqX, seqY, dotR, 0, Math.PI * 2);
          ctx.fill();
          seqX += gap;
        } else if (tok === '_') {
          // Pause indicator: short bottom line
          ctx.strokeStyle = 'rgba(100, 70, 30, 0.35)';
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(seqX - 3, seqY + dotR);
          ctx.lineTo(seqX + 3, seqY + dotR);
          ctx.stroke();
          seqX += gap * 0.7;
        }
      }

      // Description
      ctx.font = '10px Georgia, serif';
      ctx.fillStyle = '#5a4020';
      ctx.globalAlpha = 0.7;
      ctx.fillText(inv.description, cx + 12, cy + cellH - 18);
      ctx.globalAlpha = 1;
    }
  }

  // ── Arcanes page ──────────────────────────────────────────────────────────

  private _drawArcanesPage(
    ctx: CanvasRenderingContext2D,
    cX: number, cY: number, cW: number, cH: number,
  ): void {
    const cx = cX + cW / 2;

    // Section title
    ctx.font = 'small-caps bold 15px Georgia, serif';
    ctx.fillStyle = 'rgba(100, 70, 30, 0.75)';
    ctx.textBaseline = 'top';
    ctx.textAlign = 'center';
    ctx.fillText('⟡  Arcanes  ⟡', cx, cY + 4);

    ctx.font = 'italic 10px Georgia, serif';
    ctx.fillStyle = 'rgba(100, 70, 30, 0.45)';
    ctx.fillText('Symbols drawn inside a box, activated by invocation', cx, cY + 24);
    ctx.textAlign = 'left';

    // Ornamental divider
    this._drawOrnamentalDivider(ctx, cx, cY + 40, 100);

    // Grid layout: 2 columns
    const COLS = 2;
    const PAD = 14;
    const cellW = Math.floor((cW - PAD * 3) / COLS);
    const cellH = 64;
    const startY = cY + 52;

    for (let i = 0; i < this._arcanes.length; i++) {
      const arc = this._arcanes[i];
      const col = i % COLS;
      const row = Math.floor(i / COLS);
      const cx = cX + PAD + col * (cellW + PAD);
      const cy = startY + row * (cellH + 10);
      if (cy + cellH > cY + cH) break;

      // Card background
      ctx.fillStyle = 'rgba(235, 220, 190, 0.45)';
      roundRect(ctx, cx, cy, cellW, cellH, 5);
      ctx.fill();

      // Symbol box (left side, shows the rune glyph)
      const symSize = 36;
      const symX = cx + 10;
      const symY = cy + (cellH - symSize) / 2;
      ctx.strokeStyle = 'rgba(139, 99, 64, 0.25)';
      ctx.lineWidth = 1;
      roundRect(ctx, symX, symY, symSize, symSize, 3);
      ctx.stroke();

      // Symbol glyph
      ctx.font = 'bold 20px Georgia, serif';
      ctx.fillStyle = 'rgba(100, 70, 30, 0.65)';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(arc.symbol, symX + symSize / 2, symY + symSize / 2);

      // Name
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.font = 'bold 12px Georgia, serif';
      ctx.fillStyle = '#3a2510';
      ctx.fillText(arc.name, cx + 10 + symSize + 10, cy + 10);

      // Description
      ctx.font = '10px Georgia, serif';
      ctx.fillStyle = '#5a4020';
      ctx.globalAlpha = 0.7;
      ctx.fillText(arc.description, cx + 10 + symSize + 10, cy + 28);
      ctx.globalAlpha = 1;
    }
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxW: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let current = '';
  for (const w of words) {
    const test = current ? `${current} ${w}` : w;
    if (ctx.measureText(test).width > maxW) {
      if (current) { lines.push(current); }
      current = w;
    } else {
      current = test;
    }
  }
  if (current) { lines.push(current); }
  return lines;
}

/** Truncate text with ellipsis if wider than maxW. */
function truncate(ctx: CanvasRenderingContext2D, text: string, maxW: number): string {
  if (ctx.measureText(text).width <= maxW) return text;
  for (let i = text.length - 1; i > 0; i--) {
    const trial = text.slice(0, i) + '\u2026';
    if (ctx.measureText(trial).width <= maxW) return trial;
  }
  return '\u2026';
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
